// src/components/exercise/exercises/SentenceRearrangementExercise.tsx
import React, { useEffect, useMemo, useState } from 'react';
import type { Exercise, ExerciseUserInput, GradeResult } from '../../../types/exercise';
import { QuestionBox, FeedbackBox } from './TrueFalseExercise';
import { useThemePalette } from '../../../hooks/useThemePalette';
import { useThemeStore }   from '../../../stores/useThemeStore';

interface Props {
  exercise: Exercise;
  onChange: (input: ExerciseUserInput) => void;
  isSubmitted: boolean;
  grade: GradeResult | null;
}

function shuffleWords(words: string[], seed: string): string[] {
  const arr = [...words];
  for (let i = arr.length - 1; i > 0; i--) {
    const code = seed.charCodeAt(i % seed.length) + i;
    const j = code % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const SentenceRearrangementExercise: React.FC<Props> = ({ exercise, onChange, isSubmitted, grade }) => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';
  const correctWords = useMemo(() => String(exercise.correct_answer ?? '').trim().split(/\s+/).filter(Boolean), [exercise]);
  const shuffled     = useMemo(() => shuffleWords(correctWords, exercise.question), [correctWords, exercise.question]);

  const [pool,         setPool]         = useState<number[]>([]);
  const [built,        setBuilt]        = useState<string[]>([]);
  const [hoveredPool,  setHoveredPool]  = useState<number | null>(null);
  const [hoveredBuilt, setHoveredBuilt] = useState<number | null>(null);

  useEffect(() => { setPool(shuffled.map((_, i) => i)); setBuilt([]); }, [exercise, shuffled]);

  const addWord = (poolIdx: number) => {
    if (isSubmitted) return;
    const word     = shuffled[poolIdx];
    const newBuilt = [...built, word];
    setBuilt(newBuilt);
    setPool(pool.filter(i => i !== poolIdx));
    onChange({ type: 'sentence_rearrangement', value: newBuilt.join(' ') });
  };

  const removeWord = (builtIdx: number) => {
    if (isSubmitted) return;
    const word        = built[builtIdx];
    const originalIdx = shuffled.findIndex((w, i) => w === word && !pool.includes(i));
    const newBuilt    = built.filter((_, i) => i !== builtIdx);
    const newPool     = originalIdx >= 0 ? [...pool, originalIdx].sort((a, b) => a - b) : pool;
    setBuilt(newBuilt);
    setPool(newPool);
    onChange({ type: 'sentence_rearrangement', value: newBuilt.join(' ') });
  };

  const clearAll = () => {
    if (isSubmitted) return;
    setBuilt([]);
    setPool(shuffled.map((_, i) => i));
    onChange({ type: 'sentence_rearrangement', value: '' });
  };

  const isCorrectWord = (word: string, idx: number): boolean => isSubmitted && correctWords[idx] === word;

  const chipBase: React.CSSProperties = {
    padding: '6px 12px', borderRadius: '999px',
    fontSize: '0.88rem', cursor: 'pointer',
    transition: 'all 0.15s ease', border: '1.5px solid',
    fontFamily: 'inherit',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <QuestionBox text={exercise.question} />

      {/* Built sentence */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Your Sentence
          </label>
          {!isSubmitted && built.length > 0 && (
            <button
              onClick={clearAll}
              style={{ background: 'none', border: 'none', color: C.error, fontSize: '0.78rem', cursor: 'pointer', padding: '2px 6px', borderRadius: '4px', fontFamily: 'inherit' }}
            >
              ✕ Clear
            </button>
          )}
        </div>
        <div style={{
          minHeight:   '54px',
          padding:     '10px 12px',
          borderRadius:'12px',
          border:      `1.5px solid ${isSubmitted ? (grade?.isCorrect ? C.success : C.error) : 'rgba(79,140,247,0.4)'}`,
          background:  C.card,
          display:     'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center',
        }}>
          {built.length === 0 && !isSubmitted && (
            <span style={{ color: C.muted, fontSize: '0.88rem', fontStyle: 'italic' }}>
              Click words below to build the sentence…
            </span>
          )}
          {built.map((word, i) => (
            <button
              key={i}
              onClick={() => removeWord(i)}
              onMouseEnter={() => setHoveredBuilt(i)}
              onMouseLeave={() => setHoveredBuilt(null)}
              disabled={isSubmitted}
              title={isSubmitted ? undefined : 'Click to remove'}
              style={{
                ...chipBase,
                borderColor: isSubmitted
                  ? (isCorrectWord(word, i) ? C.success : C.error)
                  : (hoveredBuilt === i ? C.error : C.accent),
                background: isSubmitted
                  ? (isCorrectWord(word, i) ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)')
                  : (hoveredBuilt === i ? 'rgba(248,113,113,0.08)' : 'rgba(79,140,247,0.1)'),
                color: isSubmitted
                  ? (isCorrectWord(word, i) ? C.success : C.error)
                  : C.text,
                cursor: isSubmitted ? 'default' : 'pointer',
              }}
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {/* Word pool */}
      {!isSubmitted && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Available Words
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {pool.map((poolIdx) => (
              <button
                key={poolIdx}
                onClick={() => addWord(poolIdx)}
                onMouseEnter={() => setHoveredPool(poolIdx)}
                onMouseLeave={() => setHoveredPool(null)}
                style={{
                  ...chipBase,
                  borderColor: hoveredPool === poolIdx ? C.borderH : C.border,
                  background:  hoveredPool === poolIdx
                    ? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)')
                    : C.card,
                  color: C.text,
                }}
              >
                {shuffled[poolIdx]}
              </button>
            ))}
          </div>
        </div>
      )}

      {isSubmitted && grade && (
        <FeedbackBox
          isCorrect={grade.isCorrect}
          correctDisplay={!grade.isCorrect ? grade.correctAnswerDisplay : undefined}
        />
      )}
    </div>
  );
};

export default SentenceRearrangementExercise;
